import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, useEffect, useState } from 'react';
import {
  CommonAction,
  ReduxCommonActionType,
} from '../../store/modules/common';
import { SystemMenuId } from '../../model/system/menu';
import { Modal } from 'antd';
import { IUserListItem, IUserOnlineItem } from '../../model/api-modules';
import { getUserIdList, getOnlineStatus, kickOnline } from '../../api';
import moment from 'moment';
import { SortOrder } from 'antd/lib/table/interface';
import { CreateAccount } from './components/create-account';
import { messageError, messageSuccess } from '../../components/message';
import { isNumber } from 'lodash';
import getFormatMessage from '../../locales/get-format-message';
import { ResetPasswordModal } from './components/reset-password';
import { getLocalTime } from '../../lib';

import { AutoAntTable } from '../../components/auto-ant-table';
import {
  CommonButton,
  CommonButtonGroup,
  MainButton,
} from '../../components/button/main';
import { ICommonSupportPageProps } from '../../model';
import { wrapRequestArea } from '../../lib/http/lib';
import { IRootState } from '../../store/reducers';

interface IMergeUserListItem extends IUserListItem {
  readonly UserType?: number;
  readonly Online?: boolean;
  readonly LastAccess?: string;
}

export const AdminUser = (props: ICommonSupportPageProps) => {
  const { site } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [onlineLoading, setOnlineLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [showLogoutUserModal, setShowLogoutUserModal] =
    useState<boolean>(false);
  const [showResetPasswordModal, setShowResetPasswordModal] =
    useState<boolean>(false);
  const [userList, setUserList] = useState<ReadonlyArray<IUserListItem>>([]);
  const [clickUserID, setClickUserID] = useState<string>('');
  const [mergeUserList, setMergeUserList] = useState<
    ReadonlyArray<IMergeUserListItem>
  >([]);
  const [userOnlineList, setUserOnlineList] = useState<
    ReadonlyArray<IUserOnlineItem>
  >([]);

  const [currentRecord, setCurrentRecord] = useState<IMergeUserListItem | null>(
    null
  );
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  const menuDispatch = useDispatch<Dispatch<CommonAction>>();

  menuDispatch({
    type: ReduxCommonActionType.SELECT_MENU_ID,
    result: SystemMenuId.UserListManage,
  });

  // 获取用户列表
  const getUserList = async () => {
    setLoading(true);
    const res = await wrapRequestArea(getUserIdList, site);
    if (res) {
      // 搜索后list
      setUserList(res);
      // 合并list
      setMergeUserList(res);
    }
    setLoading(false);
  };

  const getOnlineStatusList = async () => {
    setOnlineLoading(true);
    const res = await wrapRequestArea(getOnlineStatus, site);
    if (res) {
      messageSuccess(getFormatMessage('query_status_success_10'));
      const newUserList: IMergeUserListItem[] = JSON.parse(
        JSON.stringify(userList)
      );
      const userListArr: IMergeUserListItem[] = [];
      newUserList.map((item) => {
        const resItem = res.find((it) => it.UserID === item.UserID);
        let listItem = JSON.parse(JSON.stringify(item));
        if (resItem) {
          listItem = {
            ...item,
            LastAccess: resItem.LastAccess,
            Online: resItem.Online,
          };
        }
        userListArr.push(listItem);
        return userListArr;
      });
      setMergeUserList(userListArr);
      setUserOnlineList(res);
      setOnlineLoading(false);
    } else {
      setOnlineLoading(false);
    }
  };

  useEffect(() => {
    getUserList();
  }, []);

  const clickLogoutUser = (UserID: string) => {
    setShowLogoutUserModal(true);
    setClickUserID(UserID);
  };

  const logoutUserCancel = () => {
    setShowLogoutUserModal(false);
    setClickUserID('');
  };

  const logoutUser = async () => {
    if (clickUserID) {
      setConfirmLoading(true);
      const res = await kickOnline(clickUserID);
      if (res?.code === 0) {
        messageSuccess(
          getFormatMessage('logout_success_user_id', {
            params: {
              clickUserID,
            },
          })
        );
        const newUserList: IMergeUserListItem[] = JSON.parse(
          JSON.stringify(mergeUserList)
        );
        const userListArr: IMergeUserListItem[] = [];
        newUserList.map((item) => {
          let listItem = JSON.parse(JSON.stringify(item));
          if (item.UserID === clickUserID) {
            listItem = {
              ...item,
              Online: false,
            };
          }
          userListArr.push(listItem);
          return userListArr;
        });
        setMergeUserList(userListArr);
        setClickUserID('');
        setShowLogoutUserModal(false);
      } else {
        messageError(
          getFormatMessage('logout_fail_user_id', {
            params: {
              clickUserID,
            },
          })
        );
      }
      setConfirmLoading(false);
    }
  };

  const COLUMNS: any = [
    {
      title: getFormatMessage('id'),
      dataIndex: 'id',
      key: 'id',
      width: 90,
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (a: IUserListItem, b: IUserListItem) => a.id - b.id,
      fixed: 'left',
    },
    {
      title: getFormatMessage('user_name'),
      dataIndex: 'UserName',
      key: 'UserName',
      fixed: 'left',
    },
    {
      title: getFormatMessage('email_uppercase'),
      dataIndex: 'UserID',
      key: 'UserID',
      fixed: 'left',
    },
    {
      title: getFormatMessage('user_space_type'),
      dataIndex: 'UserType',
      key: 'UserType',
      width: 120,
      render: (record: number) => {
        let text = '';
        if (record === 1) {
          text = getFormatMessage('gos');
        } else if (record === 2) {
          text = getFormatMessage('gcloud_type');
        } else if (record === 3) {
          text = getFormatMessage('ioa');
        } else if (record === 4) {
          text = getFormatMessage('email_uppercase');
        }
        return text;
      },
    },
    {
      title: getFormatMessage('status'),
      dataIndex: 'Status',
      key: 'Status',
      width: 120,
      render: (record: number) => {
        let text = '';
        if (record === 0) {
          text = getFormatMessage('freeze');
        }
        return text;
      },
    },
    {
      title: getFormatMessage('online'),
      dataIndex: 'Online',
      key: 'Online',
      width: 110,
      className: 'admin-online',
      render: (record: boolean | undefined, value: any) => {
        if (record) {
          return (
            <div>
              <div className="admin-online-text"></div>
              <div
                className="admin-online-btn"
                onClick={() => clickLogoutUser(value.UserID)}
              >
                {getFormatMessage('logout_user')}
              </div>
            </div>
          );
        }
        if (record === false) {
          return (
            <div
              style={{
                width: 10,
                height: 10,
                background: '#ccc',
                borderRadius: '50%',
                margin: '0 auto',
              }}
            ></div>
          );
        }
        return null;
      },
      sorter: (a: any, b: any) => {
        const dataItemA = userOnlineList.find(
          (item) => item.UserID === a.UserID
        );
        const dataItemB = userOnlineList.find(
          (item) => item.UserID === b.UserID
        );
        const itemSort = (item: IUserOnlineItem | undefined) => {
          if (item?.Online) {
            return 1;
          }
          if (item?.Online === false) {
            return 0;
          }
          return -1;
        };
        return itemSort(dataItemB) - itemSort(dataItemA);
      },
    },
    {
      title: getFormatMessage('last_access_time'),
      dataIndex: 'LastAccess',
      key: 'LastAccess',
      width: 150,
      render: (record: any) => {
        if (record) {
          const data = record.split('|');
          const text = Number(data.splice(0, 1)[0]);
          if (isNumber(text)) {
            return (
              <div title={data.join('|')}>
                {getLocalTime(text, userInfo?.UserSettings?.Timezone)}
              </div>
            );
          }
        }
        return null;
      },
      sorter: (a: any, b: any) => {
        const strItemA = a.LastAccess ? a.LastAccess.split('|')[0] : '';
        const strItemB = b.LastAccess ? b.LastAccess.split('|')[0] : '';
        return Number(strItemB) - Number(strItemA);
      },
    },
    {
      title: getFormatMessage('modify_time_local'),
      dataIndex: 'ModifyTime',
      key: 'ModifyTime',

      render: (text: Date) => {
        return getLocalTime(text, userInfo?.UserSettings?.Timezone);
      },
      sorter: (a: any, b: any) => {
        const dateA = moment(a.ModifyTime);
        const dateB = moment(b.ModifyTime);
        return dateB.diff(dateA);
      },
    },
    {
      title: getFormatMessage('create_time_local'),
      dataIndex: 'CreateTime',
      key: 'CreateTime',
      render: (text: Date) => {
        return getLocalTime(text, userInfo?.UserSettings?.Timezone);
      },
      sorter: (a: any, b: any) => {
        const dateA = moment(a.CreateTime);
        const dateB = moment(b.CreateTime);
        return dateB.diff(dateA);
      },
      // defaultSortOrder: 'descend',
    },
    {
      title: '操作',
      key: 'Operate',
      fixed: 'right',
      render: (_text: string, record: IMergeUserListItem) => {
        if (record.UserType === 3) {
          return null;
        }
        return (
          <CommonButtonGroup>
            <CommonButton
              type="link"
              onClick={() => handleResetPassword(record)}
            >
              重置密码
            </CommonButton>
          </CommonButtonGroup>
        );
      },
    },
  ];

  const handleResetPassword = (record: IMergeUserListItem) => {
    setCurrentRecord(record);
    setShowResetPasswordModal(true);
  };

  const oncancelReset = () => {
    setCurrentRecord(null);
    setShowResetPasswordModal(false);
  };

  return (
    <div className="admin-user-list">
      <AutoAntTable
        loading={loading}
        dataSource={mergeUserList || []}
        columns={COLUMNS}
        defaultPageSize={50}
        showTotal={true}
        configFilter={{
          filters: [
            {
              name: 'UserID',
              type: 'text',
              label: getFormatMessage('user_name'),
            },
            {
              name: 'CreateTime',
              type: 'date-range',
            },
            {
              name: 'clear',
              type: 'clear-btn',
              label: '清除筛选',
            },

            {
              name: 'online',
              render() {
                return (
                  <MainButton
                    onClick={getOnlineStatusList}
                    loading={onlineLoading}
                  >
                    {getFormatMessage('online_record')}
                  </MainButton>
                );
              },
            },
            {
              name: 'create',
              place: 'right',
              render() {
                return <CreateAccount getUserList={getUserList} />;
              },
            },
          ],
        }}
      />
      <Modal
        title={getFormatMessage('logout_user_upper')}
        open={showLogoutUserModal}
        onOk={logoutUser}
        onCancel={logoutUserCancel}
        okText={getFormatMessage('confirm')}
        confirmLoading={confirmLoading}
        maskClosable={false}
      >
        {getFormatMessage('confirm_logout')}
      </Modal>
      <ResetPasswordModal
        showResetPasswordModal={showResetPasswordModal}
        onCancel={oncancelReset}
        email={currentRecord ? currentRecord.UserID : ''}
      />
    </div>
  );
};
